import React from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { envConfig } from '../../../../utils/config';

interface CustomFormInputProps {
  value?: string | number;
  onChange?: (e: React.ChangeEvent<any>) => void;
  as?: 'input' | 'textarea';
  rows?: number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  type?: 'input' | 'textarea' | 'text' | 'number' | 'percentage' | 'file' | 'password';
  name?: string;
  size?: 'lg' | 'md' | 'sm';
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  isNumber?: boolean;
  accept?: string;
}

/* ---------------- Percentage Helpers ---------------- */

const clampPercentage = (value: string): string => {
  if (value === "") return "";

  value = value.replace(/[^0-9.]/g, "");

  const dotIndex = value.indexOf(".");
  if (dotIndex !== -1) {
    const before = value.slice(0, dotIndex + 1);
    const after = value.slice(dotIndex + 1).replace(/\./g, "");
    value = before + after;
  }

  if (value.endsWith(".")) {
    const num = parseFloat(value.slice(0, -1));

    if (!isNaN(num)) {
      if (num > 100) return "100.";
      if (num < 0) return "0.";
    }

    return value;
  }

  const parts = value.split(".");

  if (parts[1]?.length > 2) {
    value = parts[0] + "." + parts[1].slice(0, 2);
  }

  const num = parseFloat(value);

  if (!isNaN(num)) {
    if (num > 100) return "100";
    if (num < 0) return "0";
  }

  return value;
};

/* ---------------- Component ---------------- */

const CustomFormInput: React.FC<CustomFormInputProps> = ({
  value,
  onChange,
  as = 'input',
  rows = 6,
  className,
  placeholder = '',
  disabled = false,
  type = 'text',
  name = "",
  size = envConfig.DEFAULT_INPUT_FIELD_SIZE,
  prepend,
  append,
  isNumber = false,
  accept,
}) => {

  const sizeClass =
    size === "lg"
      ? "form-control-lg"
      : size === "sm"
        ? "form-control-sm"
        : "form-control-md";

  const finalClassName =
    className || `form-control form-control-solid ${sizeClass}`;

  /* ---------- Change Handler ---------- */

  const handleChange = (e: React.ChangeEvent<any>) => {
    // Normal input types
    if (type !== "percentage") {
      onChange?.(e);
      return;
    }

    // Percentage logic
    const newValue = clampPercentage(e.target.value);

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: newValue,
        name: e.target.name,
      },
    };

    onChange?.(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
  };

  /* ---------- Prevent Number Scroll ---------- */

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === "number") {
      e.currentTarget.blur();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isNumber && e.target.value === "0") {
      e.target.value = '';
    }
  };

  /* ---------- Input Mode ---------- */

  const inputMode: React.HTMLAttributes<HTMLInputElement>["inputMode"] =
    type === "percentage" || type === "number"
      ? "decimal"
      : undefined;

  /* ---------- Blur Formatting ---------- */

  const handleBlur = (e: React.FocusEvent<any>) => {
    if (type !== "percentage") return;

    let val = e.target.value;

    if (val === "") return;

    let num = parseFloat(val);

    if (!isNaN(num)) {
      const formatted = num.toFixed(2);

      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: formatted,
          name: e.target.name,
        },
      };

      onChange?.(syntheticEvent as any);
    }
  };

  const commonProps = {
    as,
    value,
    onChange: handleChange,
    onBlur: handleBlur,
    onWheel: handleWheel,
    onFocus: handleFocus,
    placeholder,
    className: finalClassName,
    disabled,
    type: type === "percentage" ? "text" : type,
    inputMode,
    name,
    autoComplete: "off",
    ...(as === "textarea" ? { rows } : {}),
    ...(type === "file" && accept ? { accept } : {}),
  };

  /* ---------- Check if we have decorations ---------- */
  const hasDecorations = !!prepend || !!append;

  /* ---------- Number Input with/without InputGroup ---------- */

  if (type === "number") {
    // If number has prepend/append, use InputGroup
    if (hasDecorations) {
      return (
        <>
          <style>
            {`
              input[type=number]::-webkit-outer-spin-button,
              input[type=number]::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }

              input[type=number] {
                -moz-appearance: textfield;
              }
            `}
          </style>
          <InputGroup>
            {prepend && (
              <InputGroup.Text>
                {prepend}
              </InputGroup.Text>
            )}
            <Form.Control {...commonProps} />
            {append && (
              <InputGroup.Text>
                {append}
              </InputGroup.Text>
            )}
          </InputGroup>
        </>
      );
    }

    // Number without decorations - simple Form.Control
    return (
      <>
        <style>
          {`
            input[type=number]::-webkit-outer-spin-button,
            input[type=number]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }

            input[type=number] {
              -moz-appearance: textfield;
            }
          `}
        </style>
        <Form.Control {...commonProps} />
      </>
    );
  }

  /* ---------- Without InputGroup ---------- */

  const basicTypes = ["text", "input", "textarea", "file", "password"];
  const isBasicType = basicTypes.includes(type);

  // Return basic Form.Control for basic types without decorations
  if (!hasDecorations && (isBasicType || type === "percentage")) {
    return <Form.Control {...commonProps} />;
  }

  /* ---------- With InputGroup ---------- */

  return (
    <InputGroup>
      {prepend && (
        <InputGroup.Text>
          {prepend}
        </InputGroup.Text>
      )}

      <Form.Control {...commonProps} />

      {type === "percentage" ? (
        <InputGroup.Text>%</InputGroup.Text>
      ) : (
        append && (
          <InputGroup.Text>
            {append}
          </InputGroup.Text>
        )
      )}
    </InputGroup>
  );
};

export default CustomFormInput;